// Geteilte Typen + Helfer für die „Alte Kundenakten"-Übersicht und -Detailseite
// (SP7). Vorher lagen sie inline in der alten Übersichtstabelle.

export type FolderContact = {
  id?: number;
  lastname?: string | null;
  firstName?: string | null;
  displayName?: string | null;
} | null;

export type Folder = {
  id: number;
  folderName: string;
  matchStatus: string;
  uploaded: boolean;
  fileCount: number | null;
  sizeBytes: string | null;
  docCount: number | null;
  contact?: FolderContact;
  candidateContactIds?: string | null;
  // SP2: Zuweisungs-/Import-Workflow
  missing?: boolean;
  importRequestedAt?: string | null;
  importedAt?: string | null;
  importError?: string | null;
  // SP7: „nicht mehr benötigt"
  notNeeded?: boolean;
  notNeededAt?: string | null;
  /** Jüngste Dateiänderung im Ordner auf dem Share (vom Sync, PO 28.09.2026). */
  lastModifiedAt?: string | null;
  /** Ordner liegt im Objektspeicher (Ablage, 01.10.2026): Explorer und Vorschau ohne Zuweisung. */
  abgelegtAt?: string | null;
  /** Letzter Abgleich Ablage → Dokumente des Hauptkontakts. */
  abgleichAt?: string | null;
  /**
   * Weitere Kontakte, denen dieser Ordner ebenfalls gehört (PO 20.09.2026).
   * `contact` bleibt der Hauptkontakt — für ihn holt der Import die Dateien vom
   * Share; die hier bekommen sie vom Server gespiegelt. `importedAt` ist der
   * Zustand DIESER Mitzuweisung, nicht des Ordners.
   */
  weitereKontakte?: Array<{
    contactId: number;
    name: string | null;
    importedAt: string | null;
    importError: string | null;
  }>;
};

/** Eine Datei eines Ordners (aus KundenaktenFile, vom Host-Sync befüllt). */
export type KundenaktenFile = {
  id: number;
  relPath: string;
  fileName: string;
  sizeBytes: string | null;
  isSchaden: boolean;
};

export type FolderDetail = { folder: Folder; files: KundenaktenFile[] };

export type Stats = {
  total: number;
  exact: number;
  ambiguous: number;
  none: number;
  manual?: number;
  uploaded: number;
  notNeeded?: number;
  /** Die drei Ansichten der Übersicht (01.10.2026), ohne „fehlt am Share". */
  offen?: number;
  zugewiesen?: number;
  nichtBenoetigt?: number;
};

// Eine Quelle für die Größen-Formatierung: die Funktion lag hier und in
// DocumentsSection.tsx doppelt, mit unterschiedlichen Signaturen. Der Re-Export
// hält die bestehenden Aufrufer (AlteKundenaktenTable, AlteKundenaktenDetail)
// unverändert.
export { humanSize } from "../../utils/fileUtils";

export const contactName = (c?: FolderContact): string =>
  c?.displayName || [c?.lastname, c?.firstName].filter(Boolean).join(", ") || "—";

/** Alle Namen einer Zuweisung, Hauptkontakt zuerst. Leer = gar nicht zugewiesen. */
export const alleKontaktNamen = (f: Folder): string[] => [
  ...(f.contact ? [contactName(f.contact)] : []),
  ...(f.weitereKontakte ?? []).map((w) => w.name || `Kontakt ${w.contactId}`),
];

/**
 * Kurzform für eine Tabellenzelle: der Hauptkontakt, und wie viele noch.
 * Ausgeschrieben stünden in der Spalte drei Namen nebeneinander, und keiner
 * wäre mehr zu lesen.
 */
export const kontaktKurz = (f: Folder): string => {
  const namen = alleKontaktNamen(f);
  if (!namen.length) return "—";
  return namen.length === 1 ? namen[0] : `${namen[0]} +${namen.length - 1}`;
};

type StatusColor = "success" | "warning" | "informative" | "brand";
export const STATUS: Record<string, { c: StatusColor; t: string }> = {
  exact: { c: "success", t: "Eindeutig" },
  ambiguous: { c: "warning", t: "Mehrdeutig" },
  none: { c: "informative", t: "Kein Match" },
  manual: { c: "brand", t: "Zugewiesen" },
};

/** Import-Zustand einer Zeile (SP2/SP3): angefordert → Worker lädt → fertig/Fehler. */
export const importState = (
  f: Folder,
): { c: "success" | "warning" | "danger"; t: string; title?: string } | null => {
  if (f.importError) return { c: "danger", t: "Import-Fehler", title: f.importError };
  if (f.importRequestedAt && !f.importedAt) return { c: "warning", t: "Import ausstehend" };
  if (f.importedAt) return { c: "success", t: "Importiert" };
  return null;
};
